<ng-container>
    <c-container class="d-flex align-items-center" [fluid]="true">
        <div class="d-flex align-items-center">
            <div class="flex">
                <img class="logo" src="assets/fa-logo-main.png" alt="FarmAdvisor">
            </div>
            <button toggle="visible" cHeaderToggler [cSidebarToggle]="sidebarId" class="ps-1">
                <svg cIcon name="cilMenu" size="lg"></svg>
            </button>
        </div>

        <div class="ms-auto d-flex align-items-center gap-2">
            @if (canSeeOrgSwitcher() && organizations().length > 1) {
                <ng-container *ngTemplateOutlet="orgDropdown"></ng-container>
                <div class="vr"></div>
            } @else if (canSeeOrgSwitcher() && organizations().length === 1) {
                <span class="small text-muted">{{ organizations()[0].name }}</span>
                <div class="vr"></div>
            }
            @if (accountOrg()?.account?.name) {
                <span class="small text-muted">{{ accountOrg()?.account?.name }}</span>
                <div class="vr"></div>
            }
            @if (roleLabel()) {
                @if (originalRole() === 'ROOT_ADMIN') {
                    <c-dropdown alignment="end">
                        <button type="button" cDropdownToggle class="role-chip-toggle"
                                [title]="userRole() !== originalRole()
                                    ? 'Viewing as ' + roleLabel() + ', but API requests are still executed with Root Admin privileges. Assumed roles only change the UI — not actual permissions.'
                                    : 'Root Admin — all API requests are executed with full system privileges.'">
                            <div class="role-chip-pill">
                                @if (userRole() !== originalRole()) {
                                    <span class="role-chip-bg">{{ originalRoleLabel() }}</span>
                                }
                                <span class="role-chip-fg"
                                      [class.is-assumed]="userRole() !== originalRole()"
                                      [class.is-root]="userRole() === originalRole()"
                                      [class.solo]="userRole() === originalRole()">{{ roleLabel() }}</span>
                            </div>
                        </button>
                        <ul cDropdownMenu class="pt-0">
                            @for (role of assumableRoles; track role) {
                                <li>
                                    <button cDropdownItem (click)="assumeRoleFromHeader(role)"
                                            class="d-flex align-items-center gap-2"
                                            [class.role-item-root]="role === 'ROOT_ADMIN'">
                                        @if (role === userRole()) {
                                            <svg cIcon name="cilCheck" size="sm" class="flex-shrink-0"
                                                 [class.text-danger]="role === 'ROOT_ADMIN'"
                                                 [class.text-primary]="role !== 'ROOT_ADMIN'"></svg>
                                        } @else {
                                            <span class="flex-shrink-0" style="width: 1rem;"></span>
                                        }
                                        {{ userApiService.roleToLabel(role) }}
                                    </button>
                                </li>
                            }
                            <li><hr class="dropdown-divider mb-0"></li>
                            <li class="role-dropdown-warning px-3 py-2 d-flex align-items-start gap-2">
                                <svg cIcon name="cilWarning" size="sm" class="text-danger flex-shrink-0 mt-1"></svg>
                                <span class="small text-muted">Root Admin has full system access. Use with care.</span>
                            </li>
                        </ul>
                    </c-dropdown>
                } @else {
                    <span class="badge rounded-pill text-bg-secondary">{{ roleLabel() }}</span>
                }
                <div class="vr"></div>
            }
            <ng-container *ngTemplateOutlet="userDropdown"></ng-container>
        </div>
    </c-container>
</ng-container>

<ng-template #orgDropdown>
    <c-dropdown alignment="end">
        <button cButton color="light" [caret]="true" cDropdownToggle class="py-1 d-flex align-items-center gap-2">
            <svg cIcon name="cilBuilding" size="sm"></svg>
            <span class="small">{{ accountOrg()?.organization?.name }}</span>
        </button>
        <ul cDropdownMenu class="pt-0">
            @for (org of organizations(); track org.org_id) {
                <li>
                    <button cDropdownItem (click)="switchOrg(org)"
                            class="d-flex align-items-center gap-2">
                        @if (org.org_id === accountOrg()?.organization?.id) {
                            <svg cIcon name="cilCheck" size="sm" class="text-primary flex-shrink-0"></svg>
                        } @else {
                            <span class="flex-shrink-0" style="width: 1rem;"></span>
                        }
                        {{ org.name }}
                    </button>
                </li>
            }
        </ul>
    </c-dropdown>
</ng-template>

<ng-template #userDropdown>
    <c-dropdown alignment="end">
        <button cButton color="light" [caret]="false" cDropdownToggle class="py-1 d-flex align-items-center gap-2">
            @if (user(); as user) {
                <c-avatar shape="rounded-1" size="sm" [src]="user.picture"></c-avatar>
                <span class="small d-none d-md-inline">{{ user.email }}</span>
            }
        </button>
        <ul cDropdownMenu class="pt-0">
            @if (canSwitchAccount()) {
                <li>
                    <button (click)="switchAccount()" cDropdownItem>
                        <svg cIcon class="me-2" name="cilFactorySlash"></svg>
                        Switch account
                    </button>
                </li>
            }
            @if (originalRole() === 'ROOT_ADMIN') {
                <li>
                    <button (click)="loseRole()" cDropdownItem>
                        <svg cIcon class="me-2" name="cilUser"></svg>
                        Switch role
                    </button>
                </li>
            }
            @if (canChangeRole() && originalRole() !== 'ROOT_ADMIN') {
                <li>
                    <button (click)="loseRole()" cDropdownItem>
                        <svg cIcon class="me-2" name="cilUser"></svg>
                        Change role
                    </button>
                </li>
            }
            @if (showThemeSelector) {
                <li><hr class="dropdown-divider mb-0"></li>
                <li><h6 class="dropdown-header">Theme</h6></li>
                @for (opt of themeOptions; track opt.value) {
                    <li>
                        <button cDropdownItem (click)="setTheme(opt.value)"
                                [active]="opt.value === currentTheme()"
                                class="d-flex align-items-center gap-2">
                            @if (opt.value === currentTheme()) {
                                <svg cIcon name="cilCheck" size="sm" class="flex-shrink-0"></svg>
                            } @else {
                                <span class="flex-shrink-0" style="width: 1rem;"></span>
                            }
                            <svg cIcon [name]="opt.icon" size="sm"></svg>
                            <span [class.fw-semibold]="opt.value === currentTheme()">{{ opt.label }}</span>
                        </button>
                    </li>
                }
            }
            <li><hr class="dropdown-divider mb-0"></li>
            <li>
                <button (click)="logout()" cDropdownItem>
                    <svg cIcon class="me-2" name="cilAccountLogout"></svg>
                    Sign out
                </button>
            </li>
        </ul>
    </c-dropdown>
</ng-template>
